import { useEffect, MutableRefObject, useRef } from "react";
import type { NextRouter } from "next/router";
import { createHandlePreventPageClose } from "@lib/sharedUtils";
import { ListingItem } from "@lib/shared/types";

export interface UsePreventPageCloseArgs {
    isFormSubmitted: MutableRefObject<boolean>;
    preventWindowClose: boolean | undefined;
    router: NextRouter;
    visitDetails: { trafficSourceNetwork?: string | null } | null | undefined;
    isLastStep: boolean;
    setSetPopupFormSubmittedFunction: (args: { function: () => void }) => void;
    handleOutsideFormSubmit: () => void;
    handleOffersPopupVisibility: () => void;
    currentCategorySlug: string | undefined;
    exitModalOffer: ListingItem[] | null | undefined;
    buyerPhoneNumber: string | null | undefined;
}

/**
 * Wires up the legacy mouse-exit + beforeunload handlers used by the new form.
 *
 * - Registers an exit-intent mousemove listener that pops the offers modal.
 * - Registers a `beforeunload` handler when `preventWindowClose` is on and the
 *   visit has a `ts` query param, except for Propel Media traffic.
 * - Removes the `beforeunload` handler on the last step.
 *
 * Behavior is preserved verbatim from the previous inline implementation,
 * including the existing dep arrays.
 */
export function usePreventPageClose({
    isFormSubmitted,
    preventWindowClose,
    router,
    visitDetails,
    isLastStep,
    setSetPopupFormSubmittedFunction,
    handleOutsideFormSubmit,
    handleOffersPopupVisibility,
    currentCategorySlug,
    exitModalOffer,
    buyerPhoneNumber,
}: UsePreventPageCloseArgs): void {
    // Store the handler in a ref so we can add/remove the same function reference
    const handlePreventPageCloseRef = useRef<
        ((e: WindowEventMap["beforeunload"]) => void) | null
    >(null);

    // Create the handler once and store it
    if (!handlePreventPageCloseRef.current) {
        handlePreventPageCloseRef.current = createHandlePreventPageClose(
            () => isFormSubmitted.current,
        );
    }

    // [SAME AS OLD FORM]
    useEffect(() => {
        setSetPopupFormSubmittedFunction({
            function: handleOutsideFormSubmit,
        });
        //[todo] check if we should move the  mouse move logic to useExitOffersModal
        const exitHandler = (e: MouseEvent) => {
            // Don't show exit modal if form already submitted
            if (isFormSubmitted.current) {
                return;
            }
            const mouseY = e.clientY;
            if (
                mouseY <= 10 &&
                visitDetails?.trafficSourceNetwork !== "Propel Media"
            ) {
                handleOffersPopupVisibility();
            }
        };

        document.body.addEventListener("mousemove", exitHandler);

        if (
            preventWindowClose &&
            router.query.ts &&
            visitDetails?.trafficSourceNetwork !== "Propel Media" &&
            handlePreventPageCloseRef.current
        ) {
            window.addEventListener(
                "beforeunload",
                handlePreventPageCloseRef.current,
            );
        }

        return () => {
            document.body.removeEventListener("mousemove", exitHandler);
            // Also remove beforeunload listener on cleanup
            if (handlePreventPageCloseRef.current) {
                window.removeEventListener(
                    "beforeunload",
                    handlePreventPageCloseRef.current,
                );
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCategorySlug, exitModalOffer, buyerPhoneNumber]);

    useEffect(() => {
        if (isLastStep && handlePreventPageCloseRef.current) {
            window.removeEventListener(
                "beforeunload",
                handlePreventPageCloseRef.current,
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLastStep]);
}
